import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Accordion, AccordionDetails, AccordionSummary, Autocomplete, Button, Drawer, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { openHelpCenter } from '../../../../services/main';
import { getGroupList } from '../../../../services/manage/groupmanagement';
import { getServiceables } from '../../../../services/sealing/android';
import { RootState } from '../../../../store';
import { setServiceables } from '../../../../store/sealing/android';
import { AppURL } from '../../../../utility/AppURL';
import { showDialog } from '../../../utils/notifications';
import AdvancedDetection from '../sealing/AdvancedDetection';
import BasicConfiguration from '../sealing/BasicConfiguration';
import EnvironmentConfiguration from '../sealing/EnvironmentConfiguration';
import { getCommonEl, onFieldsChanges } from '../sealing/SealingFunction';
import ToolsAndPackages from '../sealing/ToolsAndPackages';
import { appFrameworks, isHybridFramework } from '../sealing/data';
import { defaultPresetForm, getPresetOptions } from './PresetFunction';

interface PresetDrawerProps {
    open: boolean;
    view?: boolean;
    copy?: boolean;
    onSave: (data: any) => void;
    onClose: () => void;
    preset?: any;
}

const PresetDrawer: React.FC<PresetDrawerProps> = ({ open, onSave, onClose, preset = false, view = false, copy = false }) => {
    const { i18n, t } = useTranslation();
    const dispatch = useDispatch();
    const [nativePlanType, setNativePlanType] = useState<string | null>('E');
    const [hybridPlanType, setHybridPlanType] = useState<string | null>('E');
    const [sealingData, setSealingData] = useState<any>([]);
    const [fileType, setFileType] = useState('apk');
    const [formData, setFormData] = useState<any>(null);
    const [disableAccordion, setDisableAccordion] = useState(true);
    const [groupList, setGroupList] = useState([]);
    const serviceables = useSelector((state: RootState) => state.androidSealingReducer.serviceables);
    const userDetails = useSelector((state: RootState) => state.mainReducer.userDetails);
    const planDetails = useSelector((state: RootState) => state.mainReducer.planDetails);
    const authContext = useSelector((state: RootState) => state.mainReducer.authContext);
    const region = userDetails?.default_service_region;
    const isBetaAccount = authContext?.is_beta_account;
    const isOwner = authContext?.role === 'Owner' || authContext?.role === 'SubOwner';

    const {
        handleSubmit,
        control,
        setValue,
        getValues,
        setError,
        clearErrors,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: defaultPresetForm,
    });

    useEffect(() => {
        if (isBetaAccount) {
            const fetchGroupList = async () => {
                const params = { token: authContext.token };

                const response = await getGroupList(params);
                if (response?.result?.code === '0000' && Array.isArray(response?.data.groups)) {
                    let filterGroups = response.data.groups.filter((group: any) => group.members.some((member: any) => member.type === 'Manager' && member.userId === userDetails.user_id));
                    setGroupList(isOwner ? response.data.groups : filterGroups);

                    if (formData && formData.group_id) {
                        const getGroup = response.data.groups?.find((e: any) => e.groupId === formData.group_id);
                        if (getGroup) {
                            setValue('groupId', getGroup);
                        }
                    }
                }
            };

            fetchGroupList();
        }
    }, [authContext.token, formData, isBetaAccount, setValue]);

    useEffect(() => {
        const getAppFramework = getValues('app_framework');
        const getServiceVersion = getValues('service_version');
        if (getAppFramework && getServiceVersion) setDisableAccordion(false);
        if (!getAppFramework || !getServiceVersion) setDisableAccordion(true);
    }, [formData, getValues]);

    useEffect(() => {
        if (preset && formData === null) {
            let data: any = getPresetOptions(preset);
            const isHybrid = ['REACT_NATIVE', 'ICONIC', 'CORDOVA'].includes(data.app_framework);
            const serviceVersions = (serviceables?.serviceVersions || []).filter((e) => e.type === (isHybrid ? 'hybrid' : 'native'));
            if (serviceVersions?.length) {
                const minVersion = appFrameworks.find((e) => e.detail === data.app_framework)?.minVersion;
                let sealingVersions = (serviceables?.serviceVersions || []).filter((e) => e.type === (isHybrid ? 'hybrid' : 'native'));
                if (minVersion) sealingVersions = sealingVersions.filter((e) => e.service_version >= minVersion);
                sealingVersions = sealingVersions.sort((a, b) => b.service_version.localeCompare(a.service_version));
                setSealingData(sealingVersions);
            }

            reset(data);
            setFormData(data);
        } else if (formData === null) {
            setFormData(defaultPresetForm);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preset, formData]);

    useEffect(() => {
        if (planDetails) {
            setNativePlanType(planDetails.plan?.android?.type || 'T');
            setHybridPlanType(planDetails.plan?.hybrid?.type || 'T');
        }
    }, [planDetails]);

    useEffect(() => {
        if (serviceables === null) {
            const fetchServiceables = async () => {
                try {
                    const params = {
                        token: authContext.token,
                    };
                    const data = await getServiceables(params, region);
                    dispatch(setServiceables(data));
                } catch (error) {
                    console.error('Failed to fetch serviceables:', error);
                }
            };
            fetchServiceables();
        }
    }, [region, serviceables, authContext.token, dispatch]);

    const handleReset = async () => {
        reset(preset ? preset : defaultPresetForm);
        setFormData(null);
    };
    const onSubmit = (data: any) => {
        if (data.app_framework.length === 0) {
            setError('app_framework', { message: t('app_framework_required') }, { shouldFocus: true });
            return;
        }
        if (data.service_version.length === 0) {
            setError('service_version', { message: t('service_version_required') }, { shouldFocus: true });
            return;
        }
        if (data.service_version >= '3.0.0.0' && data.dex_string_obfuscation === 'no' && data.dex_method_call_obfuscation === 'no') {
            showDialog({
                title: t('warning'),
                content: t('dex_string_obfuscation_content_msg'),
                cancelButtonProps: { hidden: true },
                okText: t('ok'),
            });
            return;
        }
        onSave(data);
    };
    const isOTADisabled = (framework: any) => {
        let disable = true;
        const isHybrid = isHybridFramework(framework);
        if (framework) {
            if (isHybrid && hybridPlanType === 'E') disable = false;
            if (!isHybrid && nativePlanType === 'E') disable = false;
        }
        return disable;
    };

    const onFieldsChange = (fields: any) => {
        onFieldsChanges({
            fields,
            isOTADisabled,
            formData,
            setValue,
            getValues,
            appFrameworks,
            serviceables,
            setSealingData,
            nativePlanType,
            hybridPlanType,
            setFileType,
            setFormData,
        });
    };
    const ModalHeader = (
        <div className={`mb-[50px] flex md:flex-nowrap flex-wrap justify-between items-start gap-10`}>
            <div>
                <h2 className="m-0 text-2xl font-medium whitespace-nowrap">{t(preset ? (view ? 'view_preset' : 'edit_preset') : 'create_preset')}</h2>
                <p className="text-[#A0A0A1] text-xs">{t(preset ? 'edit_preset_details' : 'create_preset_details')}</p>
            </div>
            <div>
                <div className={`flex justify-end gap-4`}>
                    {!view && (
                        <Button variant="outlined" color="secondary" onClick={handleReset}>
                            <RefreshIcon />
                        </Button>
                    )}
                    <Button variant="outlined" color="secondary" onClick={onClose} className="font-medium text-[13px]">
                        {t('cancel')}
                    </Button>
                    {!view && (
                        <Button type="submit" variant="contained" color="primary" className="font-medium text-[13px]">
                            {t('save')}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );

    const getEl = (data: any, type: string) => {
        return getCommonEl(data, formData, control, onFieldsChange, serviceables, planDetails, onHelpCenterClick);
    };

    const validateInput = () => {
        let data = {
            basicSetting: true,
            features: true,
        };
        return data;
    };
    const onHelpCenterClick = (article: string, section?: boolean, helpDeskOnly = false) => {
        let redirectURL = '';
        if (helpDeskOnly) {
            redirectURL = AppURL.helpCenter + (i18n.language === 'en_US' ? 'en-us' : 'ko');
        } else {
            redirectURL = AppURL.helpCenter + (i18n.language === 'en_US' ? 'en-us' : 'ko') + (section ? '/sections/' : '/articles/') + article;
        }
        openHelpCenter(redirectURL);
    };

    const isAndroidExpired = (type: string) => {
        let isExpired = false;
        type = type === 'hybrid' ? 'hybrid' : 'android';
        if (planDetails?.plan?.[type]?.type === 'T' && planDetails?.plan?.[type]?.isExpired) isExpired = true;
        return isExpired;
    };
    const isReleaseEnabled = (framework: any) => {
        let isRelease = false;
        const isHybrid = isHybridFramework(framework);
        if (framework) {
            if (isHybrid) {
                if (hybridPlanType === 'E' || hybridPlanType === 'P') isRelease = true;
            } else {
                if (nativePlanType === 'E' || nativePlanType === 'P') isRelease = true;
            }
        }
        return isRelease;
    };
    return (
        <Drawer anchor="right" open={open} onClose={onClose} transitionDuration={1000}>
            <div className="p-10 min-w-[800px]">
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div>{ModalHeader}</div>
                    <div>
                        {isBetaAccount && (
                            <div className="mb-8">
                                <div>
                                    <p className="mb-4 font-medium text-primary">{t('group')}</p>
                                </div>
                                <div className="w-full">
                                    <Controller
                                        name="groupId"
                                        control={control}
                                        rules={{ required: t('group_required') }}
                                        render={({ field }) => (
                                            <Autocomplete
                                                {...field}
                                                options={groupList ?? []}
                                                getOptionLabel={(option: any) => option.name}
                                                isOptionEqualToValue={(option, value) => option.groupId === value.groupId}
                                                disabled={view}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        error={!!errors['groupId']}
                                                        helperText={!!errors['groupId'] ? t('group_required') : ''}
                                                        fullWidth
                                                        placeholder={t('select_group')}
                                                        variant="outlined"
                                                        size="small"
                                                        disabled={view}
                                                    />
                                                )}
                                                onChange={(_, value) => {
                                                    field.onChange(value);
                                                }}
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                        )}
                        <div className="mb-8">
                            <div>
                                <p className="mb-4 font-medium text-primary">{t('preset_name')}</p>
                            </div>
                            <div className="w-full">
                                <Controller
                                    name="presetName"
                                    control={control}
                                    rules={{ required: t('preset_name_required'), pattern: /^[a-zA-Z0-9._-]+$/ }}
                                    render={({ field }) => (
                                        <TextField
                                            error={!!errors['presetName']}
                                            {...field}
                                            helperText={!!errors['presetName'] ? t('preset_name_required') : ''}
                                            fullWidth
                                            onChange={(e) => {
                                                const value = e.target.value;
                                                if (value === '' || /^[a-zA-Z0-9._-]+$/.test(value)) {
                                                    clearErrors('presetName');
                                                    setValue('presetName', value);
                                                } else if (value.length > 1) {
                                                    setError('presetName', { message: t('preset_name_required') }, { shouldFocus: true });
                                                }
                                            }}
                                            disabled={view}
                                            inputProps={{ maxLength: 50 }}
                                            placeholder={t('preset_name_placeholder')}
                                            variant="outlined"
                                            size="small"
                                        />
                                    )}
                                />
                            </div>
                        </div>
                        <div className="mb-[24px]">
                            <div>
                                <p className="mb-4 font-medium text-primary">{t('description')}</p>
                            </div>
                            <div className="w-full">
                                <Controller
                                    name="presetDescription"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            inputProps={{ maxLength: 200 }}
                                            fullWidth
                                            placeholder={t('preset_name_desc_placeholder')}
                                            variant="outlined"
                                            size="small"
                                            multiline
                                            minRows={5}
                                            disabled={view}
                                        />
                                    )}
                                />
                            </div>
                        </div>
                    </div>
                    <div>
                        <Accordion
                            key="panel-0"
                            expanded={formData ? true : false}
                            sx={{
                                boxShadow: 'none',
                                border: 'none',
                                '&:before': {
                                    display: 'none',
                                },
                            }}
                        >
                            <AccordionSummary className="px-0 border-none" expandIcon={<ChevronRightIcon />} aria-controls="panel1-content" id="panel1-header">
                                <div className="flex items-center w-full">
                                    <span className="text-lg font-medium text-nowrap">{t('basic_configuration')}</span>
                                    <hr className="block w-full ml-4" />
                                </div>
                            </AccordionSummary>
                            <AccordionDetails>
                                <div className={`${view ? 'select-none' : ''} relative`}>
                                    {(view || copy) && <div className="absolute inset-0 z-10 bg-transparent" />}
                                    {formData && (
                                        <BasicConfiguration
                                            formData={formData}
                                            control={control}
                                            errors={errors}
                                            noTitle={true}
                                            onFieldsChange={onFieldsChange}
                                            validateInput={validateInput}
                                            isAndroidExpired={isAndroidExpired}
                                            isReleaseEnabled={isReleaseEnabled}
                                            fileType={fileType}
                                            sealingData={sealingData}
                                            serviceables={serviceables}
                                            getEl={getEl}
                                        />
                                    )}
                                </div>
                            </AccordionDetails>
                        </Accordion>
                        {fileType !== 'zip' && (
                            <Accordion
                                key="panel-1"
                                disabled={disableAccordion}
                                sx={{
                                    boxShadow: 'none',
                                    border: 'none',
                                    '&:before': {
                                        display: 'none',
                                    },
                                }}
                            >
                                <AccordionSummary className="px-0 border-none" expandIcon={<ChevronRightIcon />} aria-controls="panel1-content" id="panel1-header">
                                    <div className="flex items-center w-full">
                                        <span className="text-lg font-medium text-nowrap">{t('general_features')}</span>
                                        <span
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onHelpCenterClick('900000555906', false);
                                            }}
                                            className="mx-2 underline uppercase text-nowrap text-accent"
                                        >
                                            {t('read_more')}
                                        </span>
                                        <hr className="block w-full" />
                                    </div>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {formData && (
                                        <div className={`${view ? 'select-none' : ''}`}>
                                            {(view || copy) && <div className="absolute inset-0 z-10 bg-transparent" />}
                                            <div>
                                                <EnvironmentConfiguration getEl={getEl} />
                                            </div>
                                            <div>
                                                <ToolsAndPackages getEl={getEl} />
                                            </div>
                                        </div>
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        )}
                        {fileType !== 'zip' && (
                            <Accordion
                                key="panel-2"
                                disabled={disableAccordion}
                                sx={{
                                    boxShadow: 'none',
                                    border: 'none',
                                    '&:before': {
                                        display: 'none',
                                    },
                                }}
                            >
                                <AccordionSummary className="px-0 border-none" expandIcon={<ChevronRightIcon />} aria-controls="panel2-content" id="panel2-header">
                                    <div className="flex items-center w-full">
                                        <span className="text-lg font-medium text-nowrap">{t('advanced_features')}</span>
                                        <span
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onHelpCenterClick('11601602478361', true);
                                            }}
                                            className="mx-2 underline uppercase text-nowrap text-accent"
                                        >
                                            {t('read_more')}
                                        </span>
                                        <hr className="block w-full" />
                                    </div>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {formData && (
                                        <div className="w-full">
                                            <div className={`${view ? 'select-none' : ''}`}>
                                                {(view || copy) && <div className="absolute inset-0 z-10 bg-transparent" />}
                                                <AdvancedDetection getEl={getEl} />
                                            </div>
                                        </div>
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        )}
                    </div>
                </form>
            </div>
        </Drawer>
    );
};

export default PresetDrawer;
